import { contextScreens } from "./screens.ts";
import { nextEventAction, type NextEventAction } from "./flow.ts";
import { readinessIssue } from "./readinessIssue.ts";
import type { Context, RecordValue, Unit } from "./types";

export type ReadinessStatus = "ready" | "blocked" | "stale" | "unknown";
export type ReadinessTask = {
  title: [string, string];
  action: [string, string];
  tab: string;
  read?: string;
  write?: string;
  unitId?: string;
};

/** Only an explicit, internally consistent check for the displayed version is current. */
export function readinessStatus(data: RecordValue, unit: Unit | null): ReadinessStatus {
  if (!unit || !Number.isSafeInteger(unit.version) || data.checkedVersion !== unit.version ||
    (data.unitId !== undefined && data.unitId !== unit.unitId)) return "stale";
  if (!Array.isArray(data.issues) || !data.issues.every(issue => typeof issue === "string" && issue.length > 0)) return "unknown";
  if (data.ready === true && data.issues.length === 0) return "ready";
  if (data.ready === false && data.issues.length > 0) return "blocked";
  return "unknown";
}

/** Opens a contextual view/form only; it supplies no mutation or policy defaults. */
export function readinessTask(code: string, context: Context): ReadinessTask | null {
  if (!context.unit || context.unit.status === "ARCHIVED") return null;
  const issue = readinessIssue(code); code = issue.code;
  const task: ReadinessTask = code === "EVENT_ROUND_DATES_REQUIRED"
    ? { title: ["مواعيد الجولة", "Round dates"], action: ["تحديد مواعيد الجولة", "Set round dates"], tab: "structure", read: "unit", write: "unitUpdate" }
    : code === "EVENT_ROUND_MEDIA_REQUIRED"
      ? { title: ["وسائط الفرق في الجولة", "Round team media"], action: ["إكمال وسائط الفرق", "Complete team media"], tab: "entries", read: "entries" }
    : code === "EVENT_TEAMS_REQUIRED"
      ? { title: ["الفرق وأعضاؤها", "Teams and members"], action: ["إكمال الفرق والأعضاء", "Complete teams and members"], tab: "entries", read: "groups" }
      : code === "EVENT_STAGES_REQUIRED"
        ? { title: ["مواسم المسابقة", "Competition seasons"], action: ["مراجعة المواسم", "Review seasons"], tab: "structure" }
        : code === "EVENT_ROUNDS_REQUIRED"
          ? { title: ["جولات الموسم", "Season rounds"], action: ["مراجعة الجولات", "Review rounds"], tab: "structure" }
          : code === "EVENT_HIERARCHY_INVALID"
            ? { title: ["هيكل المسابقة", "Competition hierarchy"], action: ["مراجعة الهيكل", "Review hierarchy"], tab: "structure" }
            : code.startsWith("EVENT_ROUND_WEIGHT")
              ? { title: ["أوزان جولات الموسم", "Season round weights"], action: ["مراجعة أوزان الجولات", "Review round weights"], tab: "structure", ...(context.unit.unitKind === "VOTING_ROUND" ? { read: "unit", write: "unitUpdate" } : {}) }
              : code.startsWith("VOTING_POLICY")
    ? { title: ["قواعد التصويت", "Voting rules"], action: ["إكمال القواعد", "Complete rules"], tab: "structure", read: "votingPolicy", write: "votingPolicy" }
    : code.startsWith("FORM")
      ? { title: ["نموذج التقييم", "Evaluation form"], action: ["مراجعة النموذج", "Review form"], tab: "structure", read: "evaluationForm", write: "evaluationForm" }
      : code.startsWith("SCHEDULE")
        ? { title: ["موعد التصويت", "Voting schedule"], action: ["تحديد الموعد", "Set schedule"], tab: "structure", read: "roundLive", write: "roundSchedule" }
        : code.startsWith("ENTR")
          ? { title: ["المشاركات", "Entries"], action: ["مراجعة المشاركات", "Review entries"], tab: "entries", read: "entries" }
          : code.startsWith("RESULT")
            ? { title: ["إعداد النتيجة", "Result setup"], action: ["مراجعة الإعداد", "Review setup"], tab: "structure", read: "resultPolicy", write: "resultPolicy" }
            : { title: ["متطلب إضافي", "Additional requirement"], action: ["مراجعة الموسم", "Review season"], tab: "structure", read: "unit" };
  if (issue.unitId && issue.unitId !== context.unit.unitId) return { ...task, unitId: issue.unitId, write: undefined };
  const screen = contextScreens(context).find(screen => screen.id === task.tab);
  if (!screen) return null;
  if (!task.read && !task.write) return task;
  if (task.write && screen.writes.some(([name]) => name === task.write)) return task;
  if (task.read && screen.reads.includes(task.read)) return task.write
    ? { ...task, write: undefined, action: ["مراجعة التفاصيل", "Review details"] } : task;
  return null;
}

/** Descendant links open a read view; the target workspace rechecks live permissions. */
export function readinessTaskHref(task: ReadinessTask, context: Context): string | undefined {
  if (!task.unitId) return undefined;
  const query = new URLSearchParams({ organizerId: context.organizerId, serviceProductId: context.serviceProductId, unitId: task.unitId });
  if (task.tab !== "structure") query.set("tab", task.tab);
  if (task.read) query.set("view", task.read);
  return "/app/events?" + query;
}
/** The completion action opens the existing review flow; it never executes a transition. */
export function readinessNextAction(data: RecordValue, context: Context): NextEventAction | null {
  if (readinessStatus(data, context.unit) !== "ready") return null;
  const action = nextEventAction(context.unit, data);
  if (!action || action.read === "readiness") return null;
  const screen = contextScreens(context).find(screen => screen.id === action.tab);
  if (!screen) return null;
  if (action.write && screen.writes.some(([name]) => name === action.write)) return action;
  if (!action.write && action.read && screen.reads.includes(action.read)) return action;
  return null;
}
